import { IconCheck, IconDots, IconExternalLink, IconPencil, IconUnlink } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState } from 'react';
import { NSButton, NSCard, NSDropdown, NSInput } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { formatUTCtoLocal, formatFileSize } from 'ns_libs/formatter';
import { getFileIcon, iconColorByFilenameExtension } from 'views/DocumentLibrary/helpers';
import { IDealDocument } from 'views/DocumentLibrary/types';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import UserDisplay from 'components/UserDisplay/UserDisplay';
import './AttachmentRow.scss';

interface AttachmentRowProps {
    document: IDealDocument;
    onRename: (documentId: number, newName: string) => Promise<void>;
    onRemove: (documentId: number) => void;
    isRenameSaving?: boolean;
}

const AttachmentRow: React.FC<AttachmentRowProps> = ({
    document,
    onRename,
    onRemove,
    isRenameSaving = false,
}) => {
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [newFileName, setNewFileName] = useState(document.name);

    const trimmedNewFileName = newFileName.trim();
    const handleRename = async () => {
        if (trimmedNewFileName && trimmedNewFileName !== document.name) {
            await onRename(document.id, trimmedNewFileName);
        }
        setIsRenaming(false);
    };

    const handleOpenDocument = () => {
        if (document.s3Url) {
            window.open(document.s3Url, '_blank');
        }
    };

    const dropdownOptions: IOptionAnyValue[] = [
        {
            value: 'rename',
            label: 'Rename attachment',
            component: (
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span>Rename attachment</span>
                    <NSIcon className="ml-1" icon={IconPencil} />
                </div>
            ),
        },
        {
            value: 'remove',
            label: 'Remove from task',
            component: (
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span>Remove from task</span>
                    <NSIcon className="ml-1" icon={IconUnlink} />
                </div>
            ),
        },
    ];

    const handleDropdownSelect = (option: IOptionAnyValue) => {
        if (option.value === 'remove') {
            setShowRemoveConfirm(true);
        }
        else if (option.value === 'rename') {
            setIsRenaming(true);
            setNewFileName(document.name);
        }
    };

    const handleConfirmRemove = () => {
        onRemove(document.id);
        setShowRemoveConfirm(false);
    };

    const fileIcon = getFileIcon({ fileFormat: document.extension, isExpanded: false });
    const displayDate = formatUTCtoLocal(document.createdAt, 'MMM d, yyyy, h:mm a');
    const extensionKey = document.extension?.toLowerCase() ?? '';
    const fileIconColor = iconColorByFilenameExtension[extensionKey] ?? 'blue';

    return (
        <NSCard className="NSCard--level-2 p-2 mt-2 border">
            <div className="d-flex align-items-center">
                <div className={`rounded p-2 AttachmentRow__file-icon--${fileIconColor} ml-1 mr-2`}>
                    <NSIcon icon={fileIcon} className="text-white" />
                </div>
                <div className="flex-grow-1">
                    {isRenaming ? (
                        <div className="d-flex align-items-center gap-2">
                            <NSInput
                                name='attachmentRename'
                                type="text"
                                className="mr-1"
                                value={newFileName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFileName(e.target.value)}
                                disabled={isRenameSaving}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === 'Enter') {
                                        handleRename();
                                    } else if (e.key === 'Escape') {
                                        setIsRenaming(false);
                                        setNewFileName(document.name);
                                    }
                                }}
                            />
                            <NSButton
                                icon={IconCheck}
                                size="xs"
                                callback={handleRename}
                                disabled={isRenameSaving || !trimmedNewFileName}
                                color="primary"
                            />
                        </div>
                    ) : (
                        <>
                            <div className="d-flex align-items-center">
                                <span className="font-weight-semibold">{document.name}</span>
                                <NSButton
                                    icon={IconExternalLink}
                                    callback={handleOpenDocument}
                                    ariaLabel="Preview or download document"
                                    color="secondary"
                                    className="ml-2"
                                />
                            </div>
                            <div className="d-flex align-items-center text-muted font-12 mt-1">
                                {formatFileSize(document.size)}
                                {document.uploadedById && (
                                    <>
                                        <span className="mx-1">·</span>
                                        <span>
                                            uploaded by <UserDisplay correlationId={document.uploadedById} />
                                        </span>
                                    </>
                                )}
                                <span className="mx-1">·</span>
                                <span>{displayDate}</span>
                            </div>
                        </>
                    )}
                </div>
                <NSDropdown
                    options={dropdownOptions}
                    handleSelectOption={handleDropdownSelect}
                    customToggle={<NSButton icon={IconDots} ariaLabel="Attachment actions" />}
                    toggleClassName="bg-transparent border-0 p-0"
                    menuRight
                />
            </div>
            <ConfirmationModal
                isOpen={showRemoveConfirm}
                toggle={() => setShowRemoveConfirm(false)}
                onCancel={() => setShowRemoveConfirm(false)}
                onConfirm={handleConfirmRemove}
                modalHeaderText="Remove attachment?"
                modalFooterButtonText="Remove"
                modalFooterCancelButtonText="Cancel"
                modalFooterButtonColor="primary"
            >
                <p>
                    Remove "{document.name}" from this task?
                    The document will stay in your Document library — only the link to this task will be removed.
                </p>
            </ConfirmationModal>
        </NSCard>
    );
};

export default AttachmentRow;
